import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CheckCircle2, XCircle, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import api from '@/utils/api';

const fmt = (val) => Math.round(parseFloat(val || 0)).toLocaleString('en-IN');
const fmtRound = (val) => Math.round(parseFloat(val || 0)).toLocaleString('en-IN');
const toTitleCase = (str) => {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
};
const monthLabel = (m) => {
  if (!m) return '';
  const d = new Date(m + '-01');
  return `Salary : ${d.toLocaleString('en-IN', { month: 'long' })}, ${m.split('-')[0]}`;
};
const shortMonth = (m) => {
  if (!m) return '';
  const d = new Date(m + '-01');
  return d.toLocaleString('en-IN', { month: 'short', year: 'numeric' });
};

// Salary History Hover Popup — full breakdown with both-axis scroll
const SalaryHistoryPopup = ({ employeeId, employeeName, show, anchorRect }) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const popupRef = useRef(null);

  useEffect(() => {
    if (!show || !employeeId) { setHistory(null); return; }
    let cancelled = false;
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/salary-history/${employeeId}`);
        if (!cancelled) setHistory(res.data);
      } catch { if (!cancelled) setHistory([]); }
      finally { if (!cancelled) setLoading(false); }
    };
    fetchHistory();
    return () => { cancelled = true; };
  }, [show, employeeId]);

  if (!show || !anchorRect) return null;

  const top = anchorRect.bottom + 4;
  const left = Math.min(anchorRect.left, window.innerWidth - 720);
  const d = <span className="text-slate-300">-</span>;
  const v = (val) => (val > 0 ? fmt(val) : d);
  const vDec = (val) => (val > 0 ? parseFloat(val).toFixed(1) : d);

  return (
    <div
      ref={popupRef}
      className="fixed z-[9999] bg-white border border-slate-200 rounded-xl shadow-2xl"
      style={{ top, left, width: 700, maxHeight: 400 }}
      data-testid="salary-history-popup"
    >
      <div className="px-4 py-2 border-b border-slate-100 bg-slate-50 rounded-t-xl flex items-center justify-between">
        <div>
          <span className="font-semibold text-sm text-slate-800">{toTitleCase(employeeName)}</span>
          <span className="text-[11px] text-slate-500 ml-2">Last 6 Months Salary</span>
        </div>
      </div>
      <div className="overflow-auto" style={{ maxHeight: 350 }}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-400"></div>
          </div>
        ) : !history || history.length === 0 ? (
          <div className="py-6 text-center text-slate-400 text-sm">No salary history available</div>
        ) : (
          <table className="w-full text-[11px] whitespace-nowrap">
            <thead className="bg-slate-800 text-white sticky top-0 z-10">
              <tr>
                <th className="px-2 py-1.5 text-left font-semibold sticky left-0 bg-slate-800 z-20">Month</th>
                <th className="px-2 py-1.5 text-right font-medium">Work</th>
                <th className="px-2 py-1.5 text-right font-medium text-blue-300">W.Off</th>
                <th className="px-2 py-1.5 text-right font-medium text-emerald-300">PL</th>
                <th className="px-2 py-1.5 text-right font-medium text-red-300">LWP</th>
                <th className="px-2 py-1.5 text-right font-medium text-purple-300">ODW</th>
                <th className="px-2 py-1.5 text-right font-medium text-cyan-300">D.O.P</th>
                <th className="px-2 py-1.5 text-right font-medium border-l border-slate-600">Basic</th>
                <th className="px-2 py-1.5 text-right font-medium">Earned</th>
                <th className="px-2 py-1.5 text-right font-medium">OT Hrs</th>
                <th className="px-2 py-1.5 text-right font-medium">OT Amt</th>
                <th className="px-2 py-1.5 text-right font-medium">ODW Amt</th>
                <th className="px-2 py-1.5 text-right font-medium">Incent.</th>
                <th className="px-2 py-1.5 text-right font-medium">TA</th>
                <th className="px-2 py-1.5 text-right font-medium">HRA</th>
                <th className="px-2 py-1.5 text-right font-medium text-emerald-300">Gross</th>
                <th className="px-2 py-1.5 text-right font-medium border-l border-slate-600">PF</th>
                <th className="px-2 py-1.5 text-right font-medium">ESI</th>
                <th className="px-2 py-1.5 text-right font-medium">PTAX</th>
                <th className="px-2 py-1.5 text-right font-medium">MLWF</th>
                <th className="px-2 py-1.5 text-right font-medium">TDS</th>
                <th className="px-2 py-1.5 text-right font-medium">Adv.</th>
                <th className="px-2 py-1.5 text-right font-medium">Other</th>
                <th className="px-2 py-1.5 text-right font-medium text-rose-300">Tot.Ded</th>
                <th className="px-2 py-1.5 text-right font-semibold text-amber-300 border-l border-slate-600">Net Pay</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {history.map((r) => {
                const earned = r.basic_salary && r.total_days ? Math.round((r.basic_salary / (r.total_days || 1)) * (r.dop_days || r.days_worked || 0)) : 0;
                return (
                  <tr key={r.month} className={r.no_data ? 'bg-amber-50/60' : 'hover:bg-slate-50'}>
                    <td className="px-2 py-1.5 font-semibold text-slate-700 sticky left-0 bg-inherit z-10">{shortMonth(r.month)}</td>
                    {r.no_data ? (
                      <td colSpan={24} className="px-2 py-2 text-center text-amber-500 italic font-medium">No Data Available</td>
                    ) : (
                      <>
                        <td className="px-2 py-1.5 text-right font-mono">{r.days_worked || 0}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-blue-600">{r.weekly_offs || 0}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-emerald-600">{r.paid_leaves > 0 ? r.paid_leaves : d}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-red-600">{r.lwp > 0 ? r.lwp : d}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-purple-600">{r.odw_days > 0 ? r.odw_days : d}</td>
                        <td className="px-2 py-1.5 text-right font-mono font-bold">{r.dop_days || r.days_worked || 0}</td>
                        <td className="px-2 py-1.5 text-right font-mono border-l border-slate-100">{v(r.basic_salary)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{v(earned)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{r.ot_hours > 0 ? vDec(r.ot_hours) : d}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{v(r.ot_amount)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{v(r.off_day_amount)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{v(r.incentive)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{v(r.ta)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{v(r.hra)}</td>
                        <td className="px-2 py-1.5 text-right font-mono font-semibold text-emerald-700">{v(r.gross_salary)}</td>
                        <td className="px-2 py-1.5 text-right font-mono border-l border-slate-100">{v(r.pf)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{v(r.esi)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{v(r.ptax)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{v(r.mlwf)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{v(r.tds)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{v(r.advance)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{v(r.other_ded)}</td>
                        <td className="px-2 py-1.5 text-right font-mono font-semibold text-rose-600">{v(r.total_deductions)}</td>
                        <td className="px-2 py-1.5 text-right font-mono font-bold text-slate-900 border-l border-slate-100">₹{fmt(r.net_salary)}</td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const Approvals = ({ user }) => {
  const [salaries, setSalaries] = useState([]);
  const [employees, setEmployees] = useState({});
  const [costCentres, setCostCentres] = useState({});
  const [empTypes, setEmpTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState('approve');
  const [dialogTarget, setDialogTarget] = useState(null);
  const [comments, setComments] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterCostCentre, setFilterCostCentre] = useState('');
  const [filterDesignation, setFilterDesignation] = useState('');
  const [hoveredEmployee, setHoveredEmployee] = useState(null);
  const [hoverRect, setHoverRect] = useState(null);
  const hoverTimerRef = useRef(null);

  const handleMouseEnter = useCallback((empId, empName, e) => {
    clearTimeout(hoverTimerRef.current);
    const rect = e.currentTarget.getBoundingClientRect();
    hoverTimerRef.current = setTimeout(() => {
      setHoverRect(rect);
      setHoveredEmployee({ id: empId, name: empName });
    }, 300);
  }, []);

  const handleMouseLeave = useCallback(() => {
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      setHoveredEmployee(null);
      setHoverRect(null);
    }, 200);
  }, []);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [salRes, empRes, ccRes, typesRes] = await Promise.all([
        api.get('/salary-process-v2?source=all'),
        api.get('/employees?status=Active'),
        api.get('/cost-centres'),
        api.get('/employee-types'),
      ]);
      const empMap = {};
      empRes.data.forEach(e => { empMap[e.id] = e; });
      setEmployees(empMap);
      const ccMap = {};
      ccRes.data.forEach(c => { ccMap[c.id] = c.name; });
      setCostCentres(ccMap);
      const typeMap = {};
      typesRes.data.forEach(t => { typeMap[t.id] = t.name; });
      setEmpTypes(typeMap);
      let filtered = salRes.data;
      if (user.role === 'Manager') {
        filtered = filtered.filter(s => s.hr_status === 'Submitted' && s.manager_status === 'Pending');
      } else if (user.role === 'CEO') {
        filtered = filtered.filter(s => s.manager_status === 'Approved' && s.ceo_status === 'Pending');
      }
      setSalaries(filtered);
      setSelectedIds([]);
    } catch (error) {
      toast.error('Failed to load approvals');
    } finally { setLoading(false); }
  };

  // Filter by month, cost centre and designation
  const filtered = salaries.filter(s => {
    const monthMatch = !filterMonth || s.month === filterMonth;
    const emp = employees[s.employee_id];
    const ccName = emp ? costCentres[emp.cost_centre_id] || '' : '';
    const ccMatch = !filterCostCentre || ccName === filterCostCentre;
    const desigName = emp ? empTypes[emp.employee_type_id] || '' : '';
    const desigMatch = !filterDesignation || desigName === filterDesignation;
    return monthMatch && ccMatch && desigMatch;
  });

  const allSelected = filtered.length > 0 && filtered.every(s => selectedIds.includes(s.id));
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : filtered.map(s => s.id));
  };
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const getEndpoint = (salaryId) => {
    return user.role === 'Manager'
      ? `/salary-process-v2/${salaryId}/manager-approval`
      : `/salary-process-v2/${salaryId}/ceo-approval`;
  };

  const executeBulkAction = async (action, ids) => {
    setSubmitting(true);
    let success = 0, failed = 0;
    for (const id of ids) {
      try {
        await api.post(getEndpoint(id), { action, comments });
        success++;
      } catch (_) { failed++; }
    }
    const label = action === 'approve' ? 'approved' : 'rejected';
    toast.success(`${success} salary record(s) ${label}${failed > 0 ? `, ${failed} failed` : ''}`);
    if (action === 'approve' && success > 0) {
      const month = filterMonth || (salaries.length > 0 ? salaries[0].month : '');
      if (user.role === 'Manager') {
        try { await api.post('/notify/manager-approved', { month, count: success }); } catch (_) {}
      }
      if (user.role === 'CEO') {
        try { await api.post('/notify/ceo-approved', { month, count: success }); } catch (_) {}
      }
    }
    setComments('');
    setDialogOpen(false);
    setDialogTarget(null);
    setSubmitting(false);
    fetchData();
  };

  const openBulkDialog = (action) => {
    if (selectedIds.length === 0) { toast.error('Select at least one employee'); return; }
    setDialogAction(action);
    setDialogTarget(null);
    setComments('');
    setDialogOpen(true);
  };

  const openSingleDialog = (salary, action) => {
    setDialogAction(action);
    setDialogTarget(salary);
    setComments('');
    setDialogOpen(true);
  };

  const handleConfirm = () => {
    const ids = dialogTarget ? [dialogTarget.id] : selectedIds;
    executeBulkAction(dialogAction, ids);
  };

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filterMonth) params.append('month', filterMonth);
      if (filterCostCentre) params.append('cost_centre', filterCostCentre);
      if (filterDesignation) params.append('designation', filterDesignation);
      params.append('role', user.role);
      const response = await api.get(`/export/approvals?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Approvals_${filterMonth || 'all'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const StatusBadge = ({ status }) => {
    const colors = status === 'Approved' || status === 'Paid'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : status === 'Rejected' || status === 'Failed'
      ? 'bg-rose-50 text-rose-700 border-rose-200'
      : 'bg-amber-50 text-amber-700 border-amber-200';
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${colors}`}>{status}</span>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div></div>;
  }

  // Totals
  const totals = filtered.reduce((acc, s) => {
    const earnedBasic = s.earned_basic || Math.round((s.basic_salary / (s.total_days || 1)) * ((s.days_worked || s.days_present || 0) + (s.weekly_offs || 0) + (s.paid_leaves || 0) - (s.lwp || 0)));
    return {
    basic: acc.basic + (s.basic_salary || 0),
    earned: acc.earned + (earnedBasic || 0),
    otDays: acc.otDays + (s.ot_hours || 0),
    ot: acc.ot + (s.ot_amount || 0),
    odwDays: acc.odwDays + (s.off_day_hours || 0),
    odw: acc.odw + (s.off_day_amount || 0),
    incentive: acc.incentive + (s.allowances?.Incentive || 0),
    ta: acc.ta + (s.allowances?.['Travel Allowance'] || 0),
    hra: acc.hra + (s.allowances?.HRA || 0),
    da: acc.da + (s.allowances?.DA || s.earned_da || 0),
    conv: acc.conv + (s.allowances?.Conveyance || s.earned_conv || 0),
    gross: acc.gross + (s.gross_salary || 0),
    pf: acc.pf + (s.deductions?.PF || 0),
    esi: acc.esi + (s.deductions?.ESI || 0),
    ptax: acc.ptax + (s.deductions?.PTAX || 0),
    mlwf: acc.mlwf + (s.deductions?.MLWF || 0),
    tds: acc.tds + (s.deductions?.TDS || 0),
    adv: acc.adv + (s.deductions?.Advance || 0),
    other: acc.other + (s.deductions?.Other || 0),
    totalDed: acc.totalDed + (s.total_deductions || 0),
    net: acc.net + (s.net_salary || 0),
  }}, { basic: 0, earned: 0, otDays: 0, ot: 0, odwDays: 0, odw: 0, incentive: 0, ta: 0, hra: 0, da: 0, conv: 0, gross: 0, pf: 0, esi: 0, ptax: 0, mlwf: 0, tds: 0, adv: 0, other: 0, totalDed: 0, net: 0 });

  const uniqueCostCentres = [...new Set(Object.values(costCentres))].sort();
  const uniqueDesignations = [...new Set(Object.values(empTypes))].sort();
  const availableMonths = [...new Set(salaries.map(s => s.month))].sort().reverse();

  return (
    <div className="space-y-5" data-testid="approvals-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Approvals</h1>
          <p className="mt-1 text-base text-slate-600">
            {user.role === 'Manager' ? 'Review and approve HR submissions' : 'Final approval and payment authorization'}
            {filterMonth && <span className="ml-2 text-indigo-600 font-semibold">— {monthLabel(filterMonth)}</span>}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Button onClick={exportExcel} variant="outline" size="sm" data-testid="export-approvals-excel">
            <Download className="h-4 w-4 mr-1.5" /> Export Excel
          </Button>
          {selectedIds.length > 0 && (
            <>
              <Button onClick={() => openBulkDialog('approve')} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bulk-approve-button">
                <CheckCircle2 className="h-4 w-4 mr-1.5" />
                Approve {selectedIds.length}
              </Button>
              <Button onClick={() => openBulkDialog('reject')} disabled={submitting} variant="outline" className="text-rose-600 border-rose-300 hover:bg-rose-50" data-testid="bulk-reject-button">
                <XCircle className="h-4 w-4 mr-1.5" />
                Reject {selectedIds.length}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-wrap gap-4 items-end">
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Month</Label>
          <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}
            className="flex h-10 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 min-w-[180px]"
            data-testid="filter-month">
            <option value="">All Months</option>
            {availableMonths.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Cost Centre</Label>
          <select value={filterCostCentre} onChange={(e) => setFilterCostCentre(e.target.value)}
            className="flex h-10 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 min-w-[180px]"
            data-testid="filter-cost-centre">
            <option value="">All Cost Centres</option>
            {uniqueCostCentres.map(cc => <option key={cc} value={cc}>{cc}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">Designation</Label>
          <select value={filterDesignation} onChange={(e) => setFilterDesignation(e.target.value)}
            className="flex h-10 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 min-w-[180px]"
            data-testid="filter-designation">
            <option value="">All Designations</option>
            {uniqueDesignations.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900">All caught up!</h3>
          <p className="text-slate-600 mt-2">No pending approvals at the moment.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="approvals-table">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="px-2 py-2 text-center sticky left-0 bg-slate-800 z-10" rowSpan={2}>
                    {filtered.length > 0 && (
                      <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll}
                        className="border-white data-[state=checked]:bg-indigo-500 data-[state=checked]:border-indigo-500"
                        data-testid="select-all-checkbox" />
                    )}
                  </th>
                  <th className="px-2 py-2 text-center font-semibold" rowSpan={2}>Sr</th>
                  <th className="px-3 py-2 text-left font-semibold sticky left-8 bg-slate-800 z-10" rowSpan={2}>Employee</th>
                  <th className="px-2 py-2 text-center font-semibold border-l border-slate-600" colSpan={8}>Attendance</th>
                  <th className="px-2 py-2 text-center font-semibold border-l border-slate-600 text-emerald-300" colSpan={12}>Earnings</th>
                  <th className="px-2 py-2 text-center font-semibold border-l border-slate-600 text-rose-300" colSpan={8}>Deductions</th>
                  <th className="px-2 py-2 text-center font-semibold border-l border-slate-600 text-amber-300" rowSpan={2}>Net Pay</th>
                  <th className="px-2 py-2 text-center font-semibold border-l border-slate-600" rowSpan={2}>Action</th>
                </tr>
                <tr className="bg-slate-700 text-slate-200">
                  <th className="px-2 py-1.5 text-right font-medium border-l border-slate-600">Work</th>
                  <th className="px-2 py-1.5 text-right font-medium text-blue-300">W.Off</th>
                  <th className="px-2 py-1.5 text-right font-medium text-emerald-300">PL</th>
                  <th className="px-2 py-1.5 text-right font-medium text-red-300">LWP</th>
                  <th className="px-2 py-1.5 text-right font-medium text-purple-300">ODW</th>
                  <th className="px-2 py-1.5 text-right font-medium font-bold text-cyan-300">D.O.P.</th>
                  <th className="px-2 py-1.5 text-right font-medium">Month Days</th>
                  <th className="px-2 py-1.5 text-right font-medium">Basic</th>
                  <th className="px-2 py-1.5 text-right font-medium border-l border-slate-600">Earned</th>
                  <th className="px-2 py-1.5 text-right font-medium">OT Hrs</th>
                  <th className="px-2 py-1.5 text-right font-medium">OT Amt</th>
                  <th className="px-2 py-1.5 text-right font-medium">ODW Days</th>
                  <th className="px-2 py-1.5 text-right font-medium">ODW Amt</th>
                  <th className="px-2 py-1.5 text-right font-medium">Incent.</th>
                  <th className="px-2 py-1.5 text-right font-medium">TA</th>
                  <th className="px-2 py-1.5 text-right font-medium">HRA</th>
                  <th className="px-2 py-1.5 text-right font-medium">DA</th>
                  <th className="px-2 py-1.5 text-right font-medium">Conv</th>
                  <th className="px-2 py-1.5 text-right font-medium">Shift</th>
                  <th className="px-2 py-1.5 text-right font-medium text-emerald-300">Gross</th>
                  <th className="px-2 py-1.5 text-right font-medium border-l border-slate-600">PF</th>
                  <th className="px-2 py-1.5 text-right font-medium">ESI</th>
                  <th className="px-2 py-1.5 text-right font-medium">PTAX</th>
                  <th className="px-2 py-1.5 text-right font-medium">MLWF</th>
                  <th className="px-2 py-1.5 text-right font-medium">TDS</th>
                  <th className="px-2 py-1.5 text-right font-medium">Adv.</th>
                  <th className="px-2 py-1.5 text-right font-medium">Other</th>
                  <th className="px-2 py-1.5 text-right font-medium text-rose-300">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((s, idx) => {
                  const earnedBasic = s.earned_basic || Math.round((s.basic_salary / (s.total_days || 1)) * ((s.days_worked || s.days_present || 0) + (s.weekly_offs || 0) + (s.paid_leaves || 0) - (s.lwp || 0)));
                  const isSelected = selectedIds.includes(s.id);
                  const emp = employees[s.employee_id];
                  const designation = emp ? empTypes[emp.employee_type_id] || '' : '';
                  const dash = <span className="text-slate-300">-</span>;
                  const displayDOP = s.dop_days || ((s.days_worked || s.days_present || 0) + (s.weekly_offs || 0) + (s.paid_leaves || 0) - (s.lwp || 0) + (s.odw_days || 0));
                  const c = s.comments || {};
                  const Cmt = ({text}) => text ? <span className="block text-[11px] text-indigo-500 italic truncate max-w-[80px]" title={text}>"{text}"</span> : null;
                  return (
                    <tr key={s.id} className={`hover:bg-slate-50 transition-colors ${isSelected ? 'bg-indigo-50/60' : ''}`} data-testid={`approval-row-${s.id}`}>
                      <td className="px-2 py-2.5 text-center sticky left-0 bg-inherit z-10">
                        <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(s.id)} data-testid={`select-checkbox-${s.id}`} />
                      </td>
                      <td className="px-2 py-2.5 text-center text-slate-500 font-mono">{idx + 1}</td>
                      <td className="px-3 py-2.5 text-left whitespace-nowrap sticky left-8 bg-inherit z-10 cursor-pointer"
                        data-testid={`employee-name-${s.id}`}
                        onMouseEnter={(e) => handleMouseEnter(s.employee_id, s.employee_name, e)}
                        onMouseLeave={handleMouseLeave}
                      >
                        <span className="font-semibold text-slate-900 block hover:text-indigo-700 transition-colors">{toTitleCase(s.employee_name || 'Unknown')} <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${s.source === 'grid2' ? 'bg-violet-100 text-violet-700' : 'bg-sky-100 text-sky-700'}`} data-testid={`sp-source-${s.id}`}>{s.source === 'grid2' ? 'SP1' : 'SP2'}</span></span>
                        {designation && <span className="text-[11px] text-rose-500 font-medium block">{designation}</span>}
                        {emp?.salary_cycle && <span className="text-[11px] text-emerald-600 font-medium">{emp.salary_cycle}</span>}
                      </td>
                      <td className="px-2 py-2.5 text-right font-mono border-l border-slate-100">{s.days_worked || s.days_present || 0}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-blue-600">{s.weekly_offs || 0}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-emerald-600">{(s.paid_leaves || 0) > 0 ? s.paid_leaves : dash}<Cmt text={c.pl_comment} /></td>
                      <td className="px-2 py-2.5 text-right font-mono text-red-600">{(s.lwp || 0) > 0 ? s.lwp : dash}<Cmt text={c.lwp_comment} /></td>
                      <td className="px-2 py-2.5 text-right font-mono text-purple-600">{(s.odw_days || s.off_day_hours || 0) > 0 ? (s.odw_days || s.off_day_hours) : dash}<Cmt text={c.odw_comment} /></td>
                      <td className="px-2 py-2.5 text-right font-mono font-bold">{displayDOP}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-slate-500">{s.total_days}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{fmt(s.basic_salary)}</td>
                      <td className="px-2 py-2.5 text-right font-mono border-l border-slate-100">{fmt(earnedBasic)}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{s.ot_hours > 0 ? parseFloat(s.ot_hours).toFixed(1) : dash}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{s.ot_amount > 0 ? fmt(s.ot_amount) : dash}<Cmt text={c.ot_comment} /></td>
                      <td className="px-2 py-2.5 text-right font-mono">{s.off_day_hours > 0 ? parseFloat(s.off_day_hours).toFixed(1) : dash}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{s.off_day_amount > 0 ? fmt(s.off_day_amount) : dash}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.allowances?.Incentive || 0) > 0 ? fmt(s.allowances.Incentive) : dash}<Cmt text={c.incentive_comment} /></td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.allowances?.['Travel Allowance'] || 0) > 0 ? fmt(s.allowances['Travel Allowance']) : dash}<Cmt text={c.ta_comment} /></td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.allowances?.HRA || 0) > 0 ? fmt(s.allowances.HRA) : dash}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.allowances?.DA || s.earned_da || 0) > 0 ? fmt(s.allowances?.DA || s.earned_da) : dash}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.allowances?.Conveyance || s.earned_conv || 0) > 0 ? fmt(s.allowances?.Conveyance || s.earned_conv) : dash}</td>
                      <td className="px-2 py-2.5 text-center"><span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-50 text-violet-700">{s.shift_hours || 8}h</span></td>
                      <td className="px-2 py-2.5 text-right font-mono font-semibold text-emerald-700">{fmt(s.gross_salary)}</td>
                      <td className="px-2 py-2.5 text-right font-mono border-l border-slate-100">{(s.deductions?.PF || 0) > 0 ? fmt(s.deductions.PF) : dash}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.deductions?.ESI || 0) > 0 ? fmt(s.deductions.ESI) : dash}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.deductions?.PTAX || 0) > 0 ? fmt(s.deductions.PTAX) : dash}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.deductions?.MLWF || 0) > 0 ? fmt(s.deductions.MLWF) : dash}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.deductions?.TDS || 0) > 0 ? fmt(s.deductions.TDS) : dash}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.deductions?.Advance || 0) > 0 ? fmt(s.deductions.Advance) : dash}<Cmt text={c.advance_comment} /></td>
                      <td className="px-2 py-2.5 text-right font-mono">{(s.deductions?.Other || 0) > 0 ? fmt(s.deductions.Other) : dash}<Cmt text={c.deductions_comment} /></td>
                      <td className="px-2 py-2.5 text-right font-mono font-semibold text-rose-600">{fmt(s.total_deductions)}</td>
                      <td className="px-2 py-2.5 text-right font-mono font-bold text-slate-900 border-l border-slate-100">₹{fmtRound(s.net_salary)}</td>
                      <td className="px-2 py-2.5 text-center border-l border-slate-100">
                        <div className="flex gap-1 justify-center">
                          <button onClick={() => openSingleDialog(s, 'approve')} className="px-2 py-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 text-[11px] font-semibold" data-testid={`approve-btn-${s.id}`}>Approve</button>
                          <button onClick={() => openSingleDialog(s, 'reject')} className="px-2 py-1 rounded bg-rose-100 text-rose-700 hover:bg-rose-200 text-[11px] font-semibold" data-testid={`reject-btn-${s.id}`}>Reject</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 font-semibold text-xs border-t-2 border-slate-300">
                  <td className="px-2 py-3 sticky left-0 bg-slate-100 z-10"></td>
                  <td className="px-2 py-3"></td>
                  <td className="px-3 py-3 text-left text-slate-700 sticky left-8 bg-slate-100 z-10">TOTAL ({filtered.length})</td>
                  <td className="px-2 py-3 border-l border-slate-200"></td>
                  <td className="px-2 py-3"></td>
                  <td className="px-2 py-3"></td>
                  <td className="px-2 py-3"></td>
                  <td className="px-2 py-3"></td>
                  <td className="px-2 py-3"></td>
                  <td className="px-2 py-3"></td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.basic)}</td>
                  <td className="px-2 py-3 text-right font-mono border-l border-slate-200">{fmt(totals.earned)}</td>
                  <td className="px-2 py-3 text-right font-mono">{totals.otDays > 0 ? totals.otDays.toFixed(1) : '0'}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.ot)}</td>
                  <td className="px-2 py-3 text-right font-mono">{totals.odwDays > 0 ? totals.odwDays.toFixed(1) : '0'}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.odw)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.incentive)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.ta)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.hra)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.da)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.conv)}</td>
                  <td className="px-2 py-3"></td>
                  <td className="px-2 py-3 text-right font-mono text-emerald-700">{fmt(totals.gross)}</td>
                  <td className="px-2 py-3 text-right font-mono border-l border-slate-200">{fmt(totals.pf)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.esi)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.ptax)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.mlwf)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.tds)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.adv)}</td>
                  <td className="px-2 py-3 text-right font-mono">{fmt(totals.other)}</td>
                  <td className="px-2 py-3 text-right font-mono text-rose-600">{fmt(totals.totalDed)}</td>
                  <td className="px-2 py-3 text-right font-mono font-bold text-slate-900 border-l border-slate-200">₹{fmtRound(totals.net)}</td>
                  <td className="px-2 py-3 border-l border-slate-200"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialogAction === 'approve' ? 'Approve Salary' : 'Reject Salary'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              {dialogTarget ? (
                <>
                  <p className="text-sm font-semibold text-slate-900">{dialogTarget.employee_name}</p>
                  <p className="text-lg font-mono font-bold text-slate-900 mt-1">₹{fmtRound(dialogTarget.net_salary)}</p>
                </>
              ) : (
                <p className="text-sm font-semibold text-slate-900">{selectedIds.length} employee(s) selected</p>
              )}
            </div>
            <div>
              <Label htmlFor="comments">Comments (Optional)</Label>
              <textarea id="comments" value={comments} onChange={(e) => setComments(e.target.value)}
                className="flex min-h-[80px] w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm mt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
                placeholder="Add any comments or notes..." data-testid="comments-textarea" />
            </div>
            <Button onClick={handleConfirm} disabled={submitting}
              className={`w-full ${dialogAction === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}`}
              data-testid="confirm-action-button">
              {submitting ? 'Processing...' : `Confirm ${dialogAction === 'approve' ? 'Approval' : 'Rejection'}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Salary History Hover Popup */}
      <div
        onMouseEnter={() => clearTimeout(hoverTimerRef.current)}
        onMouseLeave={handleMouseLeave}
      >
        <SalaryHistoryPopup
          employeeId={hoveredEmployee?.id}
          employeeName={hoveredEmployee?.name}
          show={!!hoveredEmployee}
          anchorRect={hoverRect}
        />
      </div>
    </div>
  );
};

export default Approvals;
